import React from 'react';
import PropTypes from 'prop-types';
import LoadingElement from '~/components/Layout/LoadingElement';
import ScanConfigurationSearch from '../ScanConfigurationSearch';
import ScanConfigurationsList from '../ScanConfigurationsList';
import ScannerConfigurationForm from '../ScannerConfigurationForm';
import { ITEMS_PER_PAGE } from '../../types/commonTypes';
import styles from './AgentScannersView.css';

const AgentScannersView = ({
  scanConfigurations = [],
  isFetchingScanConfigurations = false,
  route,
  getPath,
  onScanConfigurationClick,
  onFormClose = () => {},
  ScanConfigurationsListComponent = ScanConfigurationsList
}) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedFilter, setSelectedFilter] = React.useState(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [showForm, setShowForm] = React.useState(route?.isAdd || false);

  // Sync showForm with route changes (e.g., when navigating to /add/ again)
  React.useEffect(() => {
    if (route?.isAdd) {
      setShowForm(true);
    }
  }, [route?.isAdd]);

  const filteredScanConfigurations = React.useMemo(() => {
    let filtered = scanConfigurations;

    // Apply search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();

      filtered = filtered.filter(
        (config) =>
          config.name?.toLowerCase().includes(term) ||
          config.targetSystem?.toLowerCase().includes(term) ||
          config.state?.toLowerCase().includes(term)
      );
    }

    // Apply status filter
    if (selectedFilter) {
      const filterValue = selectedFilter.trim().toLowerCase();

      filtered = filtered.filter(
        (config) => config.state?.trim().toLowerCase() === filterValue
      );
    }

    return filtered;
  }, [scanConfigurations, searchTerm, selectedFilter]);

  const handleSearch = React.useCallback((term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  }, []);

  const handleClearSearch = React.useCallback(() => {
    setSearchTerm('');
    setCurrentPage(1);
  }, []);

  const handleFilterChange = React.useCallback((filter) => {
    setSelectedFilter(filter);
    setCurrentPage(1);
  }, []);

  const handlePageChange = React.useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleFormSubmit = React.useCallback(() => {
    setShowForm(false);
    onFormClose();
  }, [onFormClose]);

  const handleFormCancel = React.useCallback(() => {
    setShowForm(false);
    onFormClose();
  }, [onFormClose]);

  // Show form view
  if (showForm) {
    return (
      <div className={styles.formContainer}>
        <ScannerConfigurationForm
          mode="add"
          initialData={null}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
        />
      </div>
    );
  }

  // Show list view
  return (
    <div>
      <div aria-label="Search">
        <ScanConfigurationSearch
          onSearch={handleSearch}
          onClear={handleClearSearch}
          onFilterChange={handleFilterChange}
          searchTerm={searchTerm}
          selectedFilter={selectedFilter}
        />
      </div>
      <section
        aria-live="assertive"
        aria-label="Agent Scanners"
        className={styles.searchResults}
      >
        <LoadingElement
          display-if={isFetchingScanConfigurations}
          message="Searching for Scan Configurations"
        />
        <ScanConfigurationsListComponent
          display-if={!isFetchingScanConfigurations}
          id="scan-configurations-results"
          scanConfigurations={filteredScanConfigurations}
          getPath={getPath}
          onScanConfigurationClick={onScanConfigurationClick}
          currentPage={currentPage}
          itemsPerPage={ITEMS_PER_PAGE}
          onPageChange={handlePageChange}
        />
      </section>
    </div>
  );
};

AgentScannersView.propTypes = {
  scanConfigurations: PropTypes.arrayOf(PropTypes.object),
  isFetchingScanConfigurations: PropTypes.bool,
  route: PropTypes.shape({
    isAdd: PropTypes.bool,
    isEdit: PropTypes.bool
  }),
  getPath: PropTypes.func,
  onScanConfigurationClick: PropTypes.func,
  onFormClose: PropTypes.func,
  ScanConfigurationsListComponent: PropTypes.elementType
};

export default AgentScannersView;
